import isEmpty from 'lodash/isEmpty';
import { useAppendDraftComment } from 'pages/WellGuardian/WIR/hooks';
import { getKomTechnicalWirReactiveScopeData } from 'pages/WellGuardian/WIR/utils';
import {
  WIRCreateJourneyStepsKeys,
  WIRKofMeetingTechnicalWirSections,
  WIRStageGate2Sections,
} from 'pages/WellGuardian/WIR/wir.constants';
import { useCallback, useMemo } from 'react';
import { useCountryState, useSelectedWIR } from '../../../../redux/WellGuardian/selectors';
import wellGuardianApiClient from 'services/networking/wellGuardianApiClient';
import { WIR, WIRPhaseTypes } from 'types/wellGuardian';

import { getKickOffPhaseName } from '../utils/getSectionData';

export const useSectionWellInformationData = () => {
  const wirFromState = useSelectedWIR();
  const { appendDraftComment } = useAppendDraftComment();
  const countryState = useCountryState();

  const wellInformationSectionPayload = useMemo(() => {
    const { phase, wellInformation } = getKomTechnicalWirReactiveScopeData(
      wirFromState,
      countryState,
    );
    const hudDate = wellInformation?.hudDate ?? new Date().toISOString();
    const lastHud = wellInformation?.lastHud;
    /* istanbul ignore next */
    const hudTag = wellInformation?.hudTag?.includes('-') ? '' : wellInformation?.hudTag;
    const prodDataDate = wellInformation?.prodDataDate ?? new Date().toISOString();
    const lastShutinDate = wellInformation?.lastShutinDate ?? '';
    /* istanbul ignore next */
    const choke = wellInformation?.choke?.includes('-') ? '' : wellInformation?.choke;
    /* istanbul ignore next */
    const gasRate = wellInformation?.gasRate?.includes('-') ? '' : wellInformation?.gasRate;
    /* istanbul ignore next */
    const waterRate = wellInformation?.waterRate?.includes('-') ? '' : wellInformation?.waterRate;
    /* istanbul ignore next */
    const oilRate = wellInformation?.oilRate?.includes('-') ? '' : wellInformation?.oilRate;
    /* istanbul ignore next */
    const GOR = wellInformation?.GOR?.includes('-') ? '' : wellInformation?.GOR;
    const exportRate = wellInformation?.exportRate?.includes('-')
      /* istanbul ignore next */
      ? ''
      : wellInformation?.exportRate;
    /* istanbul ignore next */
    const co2 = wellInformation?.co2?.includes('-') ? '' : wellInformation?.co2;
    const whsip = wellInformation?.whsip;
    /* istanbul ignore next */
    const whfp = wellInformation?.whfp?.includes('-') ? '' : wellInformation?.whfp;
    /* istanbul ignore next */
    const whft = wellInformation?.whft?.includes('-') ? '' : wellInformation?.whft;
    /* istanbul ignore next */
    const mewhp = wellInformation?.mewhp?.includes('-') ? '' : wellInformation?.mewhp;
    /* istanbul ignore next */
    const bht = wellInformation?.bht?.includes('-') ? '' : wellInformation?.bht;
    /* istanbul ignore next */
    const bhp = wellInformation?.bhp?.includes('-') ? '' : wellInformation?.bhp;
    const fracPressure = wellInformation?.fracPressure?.includes('-')
      /* istanbul ignore next */
      ? ''
      : wellInformation?.fracPressure;
    /* istanbul ignore next */
    const h2s = wellInformation?.h2s?.includes('-') ? '' : wellInformation?.h2s;
    const overburdendepth = wellInformation?.overburdendepth?.includes('-')
      /* istanbul ignore next */
      ? ''
      : wellInformation?.overburdendepth;
    const overburdenmewhp = wellInformation?.overburdenmewhp?.includes('-')
      /* istanbul ignore next */
      ? ''
      : wellInformation?.overburdenmewhp;
    const attachments = wellInformation?.attachments;
    const comments = appendDraftComment(wellInformation?.comments || []);
    const sectionCompleteConfirmation = wellInformation?.sectionCompleteConfirmation;

    const isTicked =
      !isEmpty(lastHud) &&
      !isEmpty(choke) &&
      !isEmpty(gasRate) &&
      !isEmpty(waterRate) &&
      !isEmpty(oilRate) &&
      !isEmpty(GOR) &&
      !isEmpty(exportRate) &&
      !isEmpty(co2) &&
      !isEmpty(whsip) &&
      !isEmpty(whfp) &&
      !isEmpty(whft) &&
      !isEmpty(mewhp) &&
      !isEmpty(bht) &&
      !isEmpty(bhp) &&
      !isEmpty(fracPressure) &&
      !isEmpty(h2s) &&
      !isEmpty(hudTag);

    return {
      hudDate,
      isTicked,
      attachments,
      hudTag,
      prodDataDate,
      lastShutinDate,
      lastHud,
      choke,
      gasRate,
      waterRate,
      oilRate,
      GOR,
      exportRate,
      co2,
      whsip,
      whfp,
      whft,
      mewhp,
      bht,
      bhp,
      fracPressure,
      h2s,
      overburdendepth,
      overburdenmewhp,
      sectionCompleteConfirmation,
      comments,
      lastUpdatedDate: new Date().toISOString(),
      assignedTo: wellInformation?.assignedTo || '',
      phase,
    };
  }, [wirFromState, appendDraftComment, countryState]);

  const saveWellInformationSection = useCallback(
    async (section: string, processSaveResponse: (wirResponse: WIR | null) => void) => {
      const phaseName = getKickOffPhaseName(countryState);
      if (
        wirFromState?.wirNumber &&
        (section === WIRKofMeetingTechnicalWirSections.wellInformation ||
          section === WIRStageGate2Sections.wellInformation)
      ) {
        processSaveResponse(
          await wellGuardianApiClient.updateWIRWellInformationSection(
            wirFromState?.wirNumber,
            wellInformationSectionPayload,
            wellInformationSectionPayload.phase === WIRCreateJourneyStepsKeys.REACTIVE_SCOPE
              ? WIRPhaseTypes.REACTIVE_SCOPE
              : phaseName,
            countryState,
          ),
        );
      }
    },
    [wirFromState, wellInformationSectionPayload, countryState],
  );

  return {
    saveWellInformationSection,
    wellInformationSectionPayload,
  };
};
